import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { FC } from 'react';
import { BeatLoader } from 'react-spinners';
import { Card } from '@/components/ui/card';
import { SectionHeadingGroup } from '@/components/ui/section-heading-group';
import { useCurrentConnectivity } from '@/hooks/use-current-connectivity';

const formatDuration = (startAt: string, endAt: string | null) => {
  const start = dayjs(startAt);
  const end = endAt ? dayjs(endAt) : dayjs();
  const d = dayjs.duration(end.diff(start));
  const days = Math.floor(d.asDays());
  const hh = d.hours().toString().padStart(2, '0');
  const mm = d.minutes().toString().padStart(2, '0');
  return days > 0 ? `${days}d ${hh}:${mm}` : `${hh}:${mm}`;
};

dayjs.extend(duration);

export const ConnectivityView: FC = () => {
  const { data, isFetching } = useCurrentConnectivity(1, 50);

  const items = data?.items ?? [];

  return (
    <div className="flex flex-row flex-wrap gap-8">
      <div className="w-min min-w-60 max-w-96 flex-grow">
        <SectionHeadingGroup
          subtitle="Recent device connectivity events for this unit."
          title="Connectivity"
        />
      </div>
      <Card className="w-min min-w-[48rem] p-6">
        {isFetching ? (
          <div className="px-6 py-4">
            <BeatLoader />
          </div>
        ) : items.length === 0 ? (
          <div className="px-6 py-4 text-xs italic text-gray-500">
            No connectivity records to display
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-fixed border-collapse text-sm">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="w-24 px-6 py-2">Status</th>
                  <th className="w-48 px-6 py-2">Device</th>
                  <th className="w-64 px-6 py-2">Start</th>
                  <th className="w-64 px-6 py-2">End</th>
                  <th className="w-32 px-6 py-2">Duration</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr className="border-t border-gray-100" key={r.id}>
                    <td className="px-6 py-2 font-medium">{r.status}</td>
                    <td className="px-6 py-2 text-gray-600">
                      {r.deviceSerialNumber ?? `ID ${r.deviceId}`}
                    </td>
                    <td className="px-6 py-2 text-gray-600">
                      {dayjs(r.startAt).format('YYYY-MM-DD HH:mm:ss')}
                    </td>
                    <td className="px-6 py-2 text-gray-600">
                      {r.endAt
                        ? dayjs(r.endAt).format('YYYY-MM-DD HH:mm:ss')
                        : '—'}
                    </td>
                    <td className="px-6 py-2 text-gray-600">
                      {formatDuration(r.startAt, r.endAt)}{' '}
                      {r.endAt ? '' : '(ongoing)'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};
